'use client';

import React, { useCallback, useRef, useState } from 'react';
import { mutate } from 'swr';
import useVerifyUser from '@/hooks/profile/useVerifyUser';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import LoginPrompt, {
    LOGIN_DIALOG_CLASS,
} from '@/components/(postTypes)/platforms/(archive)/layout/(review)/form/LoginPrompt';

/**
 * For actions the API only accepts from logged-in visitors (likes, …): ensureLogin() resolves
 * true at once when logged in; otherwise it opens a login dialog and resolves true after a
 * successful login (false if the visitor closes it). Render `dialog` once.
 */
export function useRequireLogin(reason: string) {
    const { isLoading, isLoggedInGlobal } = useVerifyUser();
    const [open, setOpen] = useState(false);
    const waiting = useRef<((ok: boolean) => void) | null>(null);

    const ensureLogin = useCallback((): Promise<boolean> => {
        if (isLoading || isLoggedInGlobal) return Promise.resolve(true);
        return new Promise((resolve) => {
            waiting.current = resolve;
            setOpen(true);
        });
    }, [isLoading, isLoggedInGlobal]);

    /** For a 401 that slipped through (expired token): ask again. */
    const askLogin = useCallback(
        (): Promise<boolean> =>
            new Promise((resolve) => {
                waiting.current = resolve;
                setOpen(true);
            }),
        [],
    );

    const finish = (ok: boolean) => {
        const resolve = waiting.current;
        waiting.current = null;
        setOpen(false);
        resolve?.(ok);
    };

    const dialog = (
        <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : finish(false))}>
            <DialogContent className={LOGIN_DIALOG_CLASS}>
                <LoginPrompt
                    reason='ابتدا وارد حساب خود شوید'
                    hint={reason}
                    onSuccess={async () => {
                        await mutate('verifyUser');
                        finish(true);
                    }}
                />
            </DialogContent>
        </Dialog>
    );

    return { ensureLogin, askLogin, dialog };
}
